// ─── ADMIN USERS ───────────────────────────────────────────────
const ADMIN_USER_PLAN_OPTIONS = [
  { value:'free', label:'Free' },
  { value:'trial', label:'Free trial' },
  { value:'pro_monthly', label:'Pro monthly' },
  { value:'pro_yearly', label:'Pro yearly' },
  { value:'referred', label:'Referred Pro' },
  { value:'custom', label:'Custom' },
  { value:'past_due', label:'Past due' },
  { value:'cancelled', label:'Cancelled' },
];

const inferAdminUserPlanType = user => {
  const sub = user?.subscription;
  if (!sub) return 'free';
  if (sub.status === 'trialing' || sub.plan === 'trial') return 'trial';
  if (sub.plan === 'custom') return 'custom';
  if (sub.plan === 'referred') return 'referred';
  if (sub.status === 'past_due') return 'past_due';
  if (sub.status === 'cancelled' || sub.status === 'expired') return 'cancelled';
  if (sub.plan === 'yearly') return 'pro_yearly';
  return 'pro_monthly';
};

const formatAdminDate = value => {
  if (!value) return '-';
  const date = new Date(String(value).includes('T') ? value : `${value}T00:00:00Z`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString([], { year:'numeric', month:'short', day:'numeric' });
};

const formatAdminTime = value => {
  if (!value) return '-';
  const date = new Date(`${value}Z`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString([], { dateStyle:'medium', timeStyle:'short' });
};

const adminSubscriptionLabel = user => {
  const sub = user?.subscription;
  if (!sub) return 'Free';
  if (sub.plan === 'custom') return 'Custom';
  if (sub.plan === 'referred') return 'Referred Pro';
  if (sub.status === 'trialing') return 'Free trial';
  if (sub.status === 'past_due') return 'Past due';
  if (sub.status === 'cancelled' || sub.status === 'expired') return sub.status === 'expired' ? 'Expired' : 'Cancelled';
  return sub.plan === 'yearly' ? 'Pro yearly' : 'Pro monthly';
};

const AdminUsersScreen = () => {
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [drafts, setDrafts] = useState({});

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    const { ok, data } = await api('GET', '/api/admin/users');
    setLoading(false);
    if (!ok) {
      setError(data?.error || 'Could not load users.');
      return;
    }
    setUsers(data.users || []);
    setSummary(data.summary || {});
    const nextDrafts = {};
    for (const user of data.users || []) {
      nextDrafts[user.id] = {
        type: inferAdminUserPlanType(user),
        currentPeriodEnd: user.subscription?.currentPeriodEnd || '',
      };
    }
    setDrafts(nextDrafts);
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const updateDraft = (id, patch) => setDrafts(prev => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));

  const saveSubscription = async user => {
    const draft = drafts[user.id] || { type: inferAdminUserPlanType(user), currentPeriodEnd: user.subscription?.currentPeriodEnd || '' };
    setSavingId(user.id);
    setError('');
    const { ok, data } = await api('PATCH', `/api/admin/users/${user.id}/subscription`, draft);
    setSavingId(null);
    if (!ok) {
      setError(data?.error || 'Could not update subscription.');
      return;
    }
    setUsers(prev => prev.map(row => row.id === user.id ? { ...row, subscription: data.subscription } : row));
    updateDraft(user.id, { type: data.subscription ? inferAdminUserPlanType({ subscription: data.subscription }) : 'free', currentPeriodEnd: data.subscription?.currentPeriodEnd || '' });
    loadUsers();
  };

  const filteredUsers = users.filter(user => {
    const q = query.trim().toLowerCase();
    const matchesQuery = !q || String(user.email || '').toLowerCase().includes(q);
    const type = inferAdminUserPlanType(user);
    const matchesFilter = filter === 'all'
      || (filter === 'active' && user.activeSessions > 0)
      || (filter === 'pro' && ['pro_monthly', 'pro_yearly'].includes(type))
      || filter === type;
    return matchesQuery && matchesFilter;
  });

  const statusBadge = user => {
    const type = inferAdminUserPlanType(user);
    const palette = {
      free: { color:'#6B7280', bg:'#F3F4F6' },
      trial: { color:'#075985', bg:'#E0F2FE' },
      pro_monthly: { color:'#065F46', bg:'#D1FAE5' },
      pro_yearly: { color:'#065F46', bg:'#D1FAE5' },
      custom: { color:'#5B21B6', bg:'#EDE9FE' },
      referred: { color:'#005BB8', bg:'#EEF6FF' },
      past_due: { color:'#92400E', bg:'#FEF3C7' },
      cancelled: { color:'#991B1B', bg:'#FEE2E2' },
    }[type] || { color:'#6B7280', bg:'#F3F4F6' };
    return <span style={{ display:'inline-flex', alignItems:'center', borderRadius:5, padding:'4px 7px', color:palette.color, background:palette.bg, fontSize:11, fontWeight:900 }}>{adminSubscriptionLabel(user)}</span>;
  };

  return (
    <div className="dash-page-scroll workspace-page">
      <div className="dash-panel workspace-panel-flat" style={{ marginBottom:14 }}>
        <div className="dash-panel-header" style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:16 }}>
          <div style={{ minWidth:0, flex:1 }}>
            <h1 className="dash-page-title">Users</h1>
            <p className="dash-page-subtitle" style={{ maxWidth:760 }}>
              Admin controls for workspace billing state, trials, Pro access, custom access, and subscription dates.
            </p>
          </div>
          <button onClick={loadUsers} disabled={loading} style={{ marginLeft:'auto', flexShrink:0, background:loading ? '#9CA3AF' : '#006FDD', color:'#fff', border:'none', borderRadius:8, padding:'9px 12px', fontSize:12, fontWeight:900, cursor:loading ? 'wait' : 'pointer', display:'flex', alignItems:'center', gap:7 }}>
            <RefreshCw size={14}/>{loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(150px, 1fr))', gap:10, marginBottom:14 }}>
        {[
          ['USERS', summary.total || 0],
          ['ACTIVE SESSIONS', summary.activeSessions || 0],
          ['PRO', summary.pro || 0],
          ['TRIALS', summary.trials || 0],
          ['CUSTOM', summary.custom || 0],
          ['FREE', summary.free || 0],
        ].map(([label, value]) => (
          <div key={label} style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:8, padding:13 }}>
            <div style={{ fontSize:11, color:'#6B7280', fontWeight:900 }}>{label}</div>
            <div style={{ fontSize:24, fontWeight:900, color:'#1A1A2E', marginTop:5 }}>{loading ? <SkeletonPill width={42} height={24}/> : value}</div>
          </div>
        ))}
      </div>

      <div className="dash-panel workspace-panel-flat" style={{ overflow:'hidden' }}>
        <div style={{ padding:'12px 14px', borderBottom:'1px solid #F3F4F6', display:'flex', gap:10, justifyContent:'space-between', alignItems:'center', flexWrap:'wrap' }}>
          <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
            <div style={{ position:'relative' }}>
              <Search size={14} style={{ position:'absolute', left:10, top:9, color:'#9CA3AF' }}/>
              <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Search users" style={{ border:'1px solid #E5E7EB', borderRadius:7, padding:'8px 10px 8px 30px', width:230, fontSize:12, outline:'none' }}/>
            </div>
            <select value={filter} onChange={e => setFilter(e.target.value)} style={{ border:'1px solid #E5E7EB', borderRadius:7, padding:'8px 9px', fontSize:12, outline:'none', background:'#fff' }}>
              <option value="all">All users</option>
              <option value="active">Active sessions</option>
              <option value="free">Free</option>
              <option value="trial">Free trial</option>
              <option value="pro">Pro</option>
              <option value="custom">Custom</option>
              <option value="past_due">Past due</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
          <div style={{ color:'#6B7280', fontSize:12, fontWeight:800 }}>{filteredUsers.length} shown</div>
        </div>

        {error && <div style={{ padding:'10px 14px', borderBottom:'1px solid #FEE2E2', background:'#FEF2F2', color:'#991B1B', fontSize:12, fontWeight:800 }}>{error}</div>}

        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', minWidth:1120, borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr style={{ background:'#F9FAFB', borderBottom:'1px solid #E5E7EB' }}>
                {['Account', 'Subscription', 'Billing', 'Expires', 'Subscribed', 'Usage', 'Access', 'Controls'].map(header => (
                  <th key={header} style={{ textAlign:'left', padding:'10px 12px', color:'#6B7280', fontSize:11, fontWeight:900 }}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ padding:18, color:'#9CA3AF' }}>Loading users...</td></tr>
              ) : filteredUsers.length === 0 ? (
                <tr><td colSpan={8} style={{ padding:18, color:'#9CA3AF' }}>No users match these filters.</td></tr>
              ) : filteredUsers.map(user => {
                const draft = drafts[user.id] || { type: inferAdminUserPlanType(user), currentPeriodEnd: user.subscription?.currentPeriodEnd || '' };
                return (
                  <tr key={user.id} style={{ borderBottom:'1px solid #F3F4F6', verticalAlign:'top' }}>
                    <td style={{ padding:'10px 12px', minWidth:230 }}>
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <div style={{ width:28, height:28, borderRadius:7, background:'#EEF6FF', color:'#006FDD', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}><UserCircle size={16}/></div>
                        <div style={{ minWidth:0 }}>
                          <div title={user.email} style={{ fontWeight:900, color:'#111827', display:'flex', gap:6, alignItems:'center', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:210 }}>{user.email}{user.isAdmin && <span style={{ color:'#6B7280', fontSize:10, flexShrink:0 }}>(ADMIN)</span>}</div>
                          <div style={{ color:'#9CA3AF', fontSize:11, marginTop:2 }}>Joined {formatAdminDate(user.createdAt)}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding:'10px 12px' }}>
                      {statusBadge(user)}
                      <div style={{ color:'#6B7280', fontSize:11, marginTop:6 }}>Plan: {user.subscription?.plan || 'none'}</div>
                    </td>
                    <td style={{ padding:'10px 12px', color:'#374151' }}>
                      <div style={{ fontWeight:800, textTransform:'capitalize' }}>{user.subscription?.billingSource || 'none'}</div>
                      <div title={user.subscription?.stripeSubscriptionId || ''} style={{ color:'#9CA3AF', fontSize:11, maxWidth:150, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user.subscription?.stripeSubscriptionId || '-'}</div>
                    </td>
                    <td style={{ padding:'10px 12px', color:'#374151', whiteSpace:'nowrap' }}>{formatAdminDate(user.subscription?.currentPeriodEnd)}</td>
                    <td style={{ padding:'10px 12px', color:'#374151', whiteSpace:'nowrap' }}>{formatAdminTime(user.subscription?.subscribedAt)}</td>
                    <td style={{ padding:'10px 12px', color:'#374151' }}>
                      <div><strong>{user.orderCount || 0}</strong> orders</div>
                      <div style={{ color:'#9CA3AF', fontSize:11 }}>Last: {formatAdminDate(user.lastOrderAt)}</div>
                    </td>
                    <td style={{ padding:'10px 12px', color:'#374151' }}>
                      <div>{user.activeSessions || 0} active session{user.activeSessions === 1 ? '' : 's'}</div>
                      <div style={{ color:'#9CA3AF', fontSize:11 }}>Last: {formatAdminTime(user.lastSessionAt)}</div>
                    </td>
                    <td style={{ padding:'10px 12px', minWidth:280 }}>
                      <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                        <select value={draft.type} onChange={e => updateDraft(user.id, { type:e.target.value })} style={{ border:'1px solid #E5E7EB', borderRadius:7, padding:'7px 9px', fontSize:12, outline:'none', background:'#fff' }}>
                          {ADMIN_USER_PLAN_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                        </select>
                        <input type="date" value={draft.currentPeriodEnd || ''} onChange={e => updateDraft(user.id, { currentPeriodEnd:e.target.value })} disabled={draft.type === 'free'} style={{ border:'1px solid #E5E7EB', borderRadius:7, padding:'6px 8px', fontSize:12, outline:'none', background:draft.type === 'free' ? '#F9FAFB' : '#fff', color:draft.type === 'free' ? '#9CA3AF' : '#111827' }}/>
                        <button onClick={() => saveSubscription(user)} disabled={savingId === user.id} style={{ background:savingId === user.id ? '#9CA3AF' : '#006FDD', color:'#fff', border:'none', borderRadius:7, padding:'8px 10px', fontSize:12, fontWeight:900, cursor:savingId === user.id ? 'wait' : 'pointer' }}>
                          {savingId === user.id ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// ─── ADMIN PARTNERS ────────────────────────────────────────────
const PARTNER_ADMIN_DEFAULT_CLIENT_LIMIT = 20;
const PARTNER_ROLE_OPTIONS = [
  { value:'owner', label:'Owner' },
  { value:'admin', label:'Admin' },
  { value:'staff', label:'Staff' },
];

const partnerDraftFor = partner => ({
  name: partner?.name || '',
  status: partner?.status || 'active',
  clientLimit: String(partner?.clientLimit ?? PARTNER_ADMIN_DEFAULT_CLIENT_LIMIT),
  subscriptionSeatLimit: String(partner?.subscriptionSeatLimit ?? 0),
});

const clampPartnerNumber = (value, fallback, min = 0) => {
  const parsed = Number.parseInt(value, 10);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.max(min, Math.min(500, parsed));
};

const AdminPartnersScreen = () => {
  const [partners, setPartners] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');
  const [drafts, setDrafts] = useState({});
  const [userDrafts, setUserDrafts] = useState({});
  const [newPartner, setNewPartner] = useState({
    name:'',
    ownerEmail:'',
    clientLimit:String(PARTNER_ADMIN_DEFAULT_CLIENT_LIMIT),
    subscriptionSeatLimit:'0',
  });

  const loadPartners = useCallback(async () => {
    setLoading(true);
    setError('');
    const { ok, data } = await api('GET', '/api/admin/partners');
    setLoading(false);
    if (!ok) {
      setError(data?.error || 'Could not load partner users.');
      return;
    }
    const nextPartners = data.partners || [];
    setPartners(nextPartners);
    setSummary(data.summary || {});
    const nextDrafts = {};
    const nextUserDrafts = {};
    for (const partner of nextPartners) {
      nextDrafts[partner.id] = partnerDraftFor(partner);
      nextUserDrafts[partner.id] = { email:'', role:'staff' };
    }
    setDrafts(nextDrafts);
    setUserDrafts(nextUserDrafts);
  }, []);

  useEffect(() => { loadPartners(); }, [loadPartners]);

  const updateDraft = (id, patch) => setDrafts(prev => ({ ...prev, [id]: { ...(prev[id] || {}), ...patch } }));
  const updateUserDraft = (id, patch) => setUserDrafts(prev => ({ ...prev, [id]: { ...(prev[id] || { email:'', role:'staff' }), ...patch } }));

  const createPartner = async event => {
    event.preventDefault();
    setError('');
    if (!newPartner.name.trim()) return setError('Partner name is required.');
    if (!newPartner.ownerEmail.trim()) return setError('Owner email is required.');
    setSavingId('new');
    const { ok, data } = await api('POST', '/api/admin/partners', {
      name: newPartner.name,
      ownerEmail: newPartner.ownerEmail,
      clientLimit: clampPartnerNumber(newPartner.clientLimit, PARTNER_ADMIN_DEFAULT_CLIENT_LIMIT, 1),
      subscriptionSeatLimit: clampPartnerNumber(newPartner.subscriptionSeatLimit, 0, 0),
    });
    setSavingId(null);
    if (!ok) {
      setError(data?.error || 'Could not create partner workspace.');
      return;
    }
    setNewPartner({ name:'', ownerEmail:'', clientLimit:String(PARTNER_ADMIN_DEFAULT_CLIENT_LIMIT), subscriptionSeatLimit:'0' });
    loadPartners();
  };

  const savePartner = async partner => {
    const draft = drafts[partner.id] || partnerDraftFor(partner);
    if (!draft.name.trim()) {
      setError('Partner name is required.');
      return;
    }
    setSavingId(`partner-${partner.id}`);
    setError('');
    const { ok, data } = await api('PATCH', `/api/admin/partners/${partner.id}`, {
      name: draft.name,
      status: draft.status,
      clientLimit: clampPartnerNumber(draft.clientLimit, PARTNER_ADMIN_DEFAULT_CLIENT_LIMIT, 1),
      subscriptionSeatLimit: clampPartnerNumber(draft.subscriptionSeatLimit, 0, 0),
    });
    setSavingId(null);
    if (!ok) {
      setError(data?.error || 'Could not update partner workspace.');
      return;
    }
    setPartners(prev => prev.map(row => row.id === partner.id ? { ...row, ...(data.partner || {}) } : row));
    loadPartners();
  };

  const addPartnerUser = async (partner, event) => {
    event.preventDefault();
    const draft = userDrafts[partner.id] || { email:'', role:'staff' };
    if (!draft.email.trim()) {
      setError('Partner user email is required.');
      return;
    }
    setSavingId(`add-user-${partner.id}`);
    setError('');
    const { ok, data } = await api('POST', `/api/admin/partners/${partner.id}/users`, {
      email: draft.email,
      role: draft.role,
    });
    setSavingId(null);
    if (!ok) {
      setError(data?.error || 'Could not add partner user.');
      return;
    }
    updateUserDraft(partner.id, { email:'', role:'staff' });
    loadPartners();
  };

  const updatePartnerUserRole = async (partner, user, role) => {
    setSavingId(`user-${partner.id}-${user.userId}`);
    setError('');
    const { ok, data } = await api('POST', `/api/admin/partners/${partner.id}/users`, {
      email: user.email,
      role,
    });
    setSavingId(null);
    if (!ok) {
      setError(data?.error || 'Could not update partner user role.');
      return;
    }
    loadPartners();
  };

  const removePartnerUser = async (partner, user) => {
    if (!window.confirm(`Remove ${user.email} from ${partner.name}?`)) return;
    setSavingId(`user-${partner.id}-${user.userId}`);
    setError('');
    const { ok, data } = await api('DELETE', `/api/admin/partners/${partner.id}/users/${user.userId}`);
    setSavingId(null);
    if (!ok) {
      setError(data?.error || 'Could not remove partner user.');
      return;
    }
    loadPartners();
  };

  const filteredPartners = partners.filter(partner => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return String(partner.name || '').toLowerCase().includes(q)
      || String(partner.slug || '').toLowerCase().includes(q)
      || (partner.users || []).some(user => String(user.email || '').toLowerCase().includes(q));
  });

  const inputStyle = { border:'1px solid #E5E7EB', borderRadius:7, padding:'8px 9px', fontSize:12, outline:'none', background:'#fff', color:'#111827' };
  const labelStyle = { display:'block', fontSize:11, color:'#6B7280', fontWeight:900, marginBottom:5 };

  return (
    <div className="dash-page-scroll workspace-page">
      <div className="dash-panel workspace-panel-flat" style={{ marginBottom:14 }}>
        <div className="dash-panel-header" style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:16 }}>
          <div style={{ minWidth:0, flex:1 }}>
            <h1 className="dash-page-title">Partner Users</h1>
            <p className="dash-page-subtitle" style={{ maxWidth:780 }}>
              Manage partner workspaces, assigned portal users, Omnifill client slots, and referred Pro access seats.
            </p>
          </div>
          <button onClick={loadPartners} disabled={loading} style={{ marginLeft:'auto', flexShrink:0, background:loading ? '#9CA3AF' : '#006FDD', color:'#fff', border:'none', borderRadius:8, padding:'9px 12px', fontSize:12, fontWeight:900, cursor:loading ? 'wait' : 'pointer', display:'flex', alignItems:'center', gap:7 }}>
            <RefreshCw size={14}/>{loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px, 1fr))', gap:10, marginBottom:14 }}>
        {[
          ['PARTNERS', summary.total || 0],
          ['ACTIVE', summary.active || 0],
          ['CLIENT SLOTS', `${summary.usedClients || 0}/${summary.clientLimit || 0}`],
          ['PRO REFERRALS', `${summary.proReferralsUsed || 0}/${summary.proReferralLimit || 0}`],
        ].map(([label, value]) => (
          <div key={label} style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:8, padding:13 }}>
            <div style={{ fontSize:11, color:'#6B7280', fontWeight:900 }}>{label}</div>
            <div style={{ fontSize:24, fontWeight:900, color:'#1A1A2E', marginTop:5 }}>{loading ? <SkeletonPill width={54} height={24}/> : value}</div>
          </div>
        ))}
      </div>

      <form onSubmit={createPartner} className="dash-panel workspace-panel-flat" style={{ marginBottom:14, padding:14 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, marginBottom:12, flexWrap:'wrap' }}>
          <div>
            <h2 style={{ margin:0, fontSize:15, color:'#111827', fontWeight:900 }}>Add partner workspace</h2>
            <div style={{ color:'#6B7280', fontSize:12, marginTop:3 }}>The owner account must already exist in Omnifill.</div>
          </div>
          <button type="submit" disabled={savingId === 'new'} style={{ background:savingId === 'new' ? '#9CA3AF' : '#006FDD', color:'#fff', border:'none', borderRadius:7, padding:'8px 10px', fontSize:12, fontWeight:900, cursor:savingId === 'new' ? 'wait' : 'pointer', display:'inline-flex', alignItems:'center', gap:6 }}>
            <Plus size={14}/>{savingId === 'new' ? 'Creating...' : 'Create'}
          </button>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:10, alignItems:'end' }}>
          <label>
            <span style={labelStyle}>Partner name</span>
            <input value={newPartner.name} onChange={event => setNewPartner(prev => ({ ...prev, name:event.target.value }))} placeholder="Warehouse or 3PL name" style={{ ...inputStyle, width:'100%' }}/>
          </label>
          <label>
            <span style={labelStyle}>Owner email</span>
            <input type="email" value={newPartner.ownerEmail} onChange={event => setNewPartner(prev => ({ ...prev, ownerEmail:event.target.value }))} placeholder="owner@example.com" style={{ ...inputStyle, width:'100%' }}/>
          </label>
          <label>
            <span style={labelStyle}>Client slots</span>
            <input type="number" min="1" max="500" value={newPartner.clientLimit} onChange={event => setNewPartner(prev => ({ ...prev, clientLimit:event.target.value }))} style={{ ...inputStyle, width:'100%' }}/>
          </label>
          <label>
            <span style={labelStyle}>Pro referrals</span>
            <input type="number" min="0" max="500" value={newPartner.subscriptionSeatLimit} onChange={event => setNewPartner(prev => ({ ...prev, subscriptionSeatLimit:event.target.value }))} style={{ ...inputStyle, width:'100%' }}/>
          </label>
        </div>
      </form>

      <div className="dash-panel workspace-panel-flat" style={{ overflow:'hidden' }}>
        <div style={{ padding:'12px 14px', borderBottom:'1px solid #F3F4F6', display:'flex', gap:10, justifyContent:'space-between', alignItems:'center', flexWrap:'wrap' }}>
          <div style={{ position:'relative' }}>
            <Search size={14} style={{ position:'absolute', left:10, top:9, color:'#9CA3AF' }}/>
            <input value={query} onChange={event => setQuery(event.target.value)} placeholder="Search partners or users" style={{ border:'1px solid #E5E7EB', borderRadius:7, padding:'8px 10px 8px 30px', width:260, fontSize:12, outline:'none' }}/>
          </div>
          <div style={{ color:'#6B7280', fontSize:12, fontWeight:800 }}>{filteredPartners.length} shown</div>
        </div>

        {error && <div style={{ padding:'10px 14px', borderBottom:'1px solid #FEE2E2', background:'#FEF2F2', color:'#991B1B', fontSize:12, fontWeight:800 }}>{error}</div>}

        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', minWidth:1180, borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr style={{ background:'#F9FAFB', borderBottom:'1px solid #E5E7EB' }}>
                {['Partner', 'Omnifill clients', 'Pro referrals', 'Partner users', 'Save'].map(header => (
                  <th key={header} style={{ textAlign:'left', padding:'10px 12px', color:'#6B7280', fontSize:11, fontWeight:900 }}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={5} style={{ padding:18, color:'#9CA3AF' }}>Loading partner users...</td></tr>
              ) : filteredPartners.length === 0 ? (
                <tr><td colSpan={5} style={{ padding:18, color:'#9CA3AF' }}>No partners match these filters.</td></tr>
              ) : filteredPartners.map(partner => {
                const draft = drafts[partner.id] || partnerDraftFor(partner);
                const userDraft = userDrafts[partner.id] || { email:'', role:'staff' };
                const partnerSaving = savingId === `partner-${partner.id}`;
                return (
                  <tr key={partner.id} style={{ borderBottom:'1px solid #F3F4F6', verticalAlign:'top' }}>
                    <td style={{ padding:'10px 12px', minWidth:270 }}>
                      <div style={{ display:'grid', gap:8 }}>
                        <input value={draft.name} onChange={event => updateDraft(partner.id, { name:event.target.value })} style={{ ...inputStyle, width:'100%', fontWeight:900 }}/>
                        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
                          <select value={draft.status} onChange={event => updateDraft(partner.id, { status:event.target.value })} style={inputStyle}>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                          <span style={{ color:'#9CA3AF', fontSize:11, fontWeight:800 }}>{partner.slug}</span>
                        </div>
                        <div style={{ color:'#9CA3AF', fontSize:11 }}>Created {formatAdminDate(partner.createdAt)}</div>
                      </div>
                    </td>
                    <td style={{ padding:'10px 12px', minWidth:180 }}>
                      <label>
                        <span style={labelStyle}>Max client slots</span>
                        <input type="number" min="1" max="500" value={draft.clientLimit} onChange={event => updateDraft(partner.id, { clientLimit:event.target.value })} style={{ ...inputStyle, width:110 }}/>
                      </label>
                      <div style={{ color:'#374151', fontSize:12, fontWeight:800, marginTop:8 }}>{partner.usedClients || 0}/{partner.clientLimit || 0} used</div>
                      <div style={{ color:'#9CA3AF', fontSize:11, marginTop:2 }}>{partner.activeClients || 0} active, {partner.pendingClients || 0} pending</div>
                    </td>
                    <td style={{ padding:'10px 12px', minWidth:170 }}>
                      <label>
                        <span style={labelStyle}>Max Pro referrals</span>
                        <input type="number" min="0" max="500" value={draft.subscriptionSeatLimit} onChange={event => updateDraft(partner.id, { subscriptionSeatLimit:event.target.value })} style={{ ...inputStyle, width:110 }}/>
                      </label>
                      <div style={{ color:'#374151', fontSize:12, fontWeight:800, marginTop:8 }}>{partner.subscriptionClients || 0}/{partner.subscriptionSeatLimit || 0} used</div>
                    </td>
                    <td style={{ padding:'10px 12px', minWidth:380 }}>
                      <div style={{ display:'grid', gap:8 }}>
                        {(partner.users || []).length === 0 ? (
                          <div style={{ color:'#9CA3AF', fontSize:12 }}>No partner users assigned.</div>
                        ) : (partner.users || []).map(user => {
                          const userSaving = savingId === `user-${partner.id}-${user.userId}`;
                          return (
                            <div key={user.userId} style={{ display:'grid', gridTemplateColumns:'minmax(150px, 1fr) 105px 34px', gap:7, alignItems:'center' }}>
                              <div title={user.email} style={{ minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontWeight:900, color:'#111827', display:'flex', alignItems:'center', gap:7 }}>
                                <UserCircle size={14} style={{ color:'#006FDD', flexShrink:0 }}/>
                                <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{user.email}</span>
                              </div>
                              <select value={user.role} onChange={event => updatePartnerUserRole(partner, user, event.target.value)} disabled={userSaving} style={{ ...inputStyle, padding:'6px 7px' }}>
                                {PARTNER_ROLE_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                              </select>
                              <button type="button" onClick={() => removePartnerUser(partner, user)} disabled={userSaving} title="Remove partner user" style={{ width:32, height:32, border:'1px solid #FCA5A5', background:'#fff', color:'#DC2626', borderRadius:7, cursor:userSaving ? 'wait' : 'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center' }}>
                                <Trash2 size={13}/>
                              </button>
                            </div>
                          );
                        })}
                        <form onSubmit={event => addPartnerUser(partner, event)} style={{ display:'grid', gridTemplateColumns:'minmax(170px, 1fr) 105px 76px', gap:7, alignItems:'center', paddingTop:4 }}>
                          <input type="email" value={userDraft.email} onChange={event => updateUserDraft(partner.id, { email:event.target.value })} placeholder="Add user email" style={inputStyle}/>
                          <select value={userDraft.role} onChange={event => updateUserDraft(partner.id, { role:event.target.value })} style={inputStyle}>
                            {PARTNER_ROLE_OPTIONS.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                          </select>
                          <button type="submit" disabled={savingId === `add-user-${partner.id}`} style={{ height:34, background:savingId === `add-user-${partner.id}` ? '#9CA3AF' : '#006FDD', color:'#fff', border:'none', borderRadius:7, padding:'0 10px', fontSize:12, fontWeight:900, cursor:savingId === `add-user-${partner.id}` ? 'wait' : 'pointer' }}>
                            Add
                          </button>
                        </form>
                      </div>
                    </td>
                    <td style={{ padding:'10px 12px', minWidth:95 }}>
                      <button onClick={() => savePartner(partner)} disabled={partnerSaving} style={{ background:partnerSaving ? '#9CA3AF' : '#006FDD', color:'#fff', border:'none', borderRadius:7, padding:'8px 10px', fontSize:12, fontWeight:900, cursor:partnerSaving ? 'wait' : 'pointer', display:'inline-flex', alignItems:'center', gap:6 }}>
                        <Save size={13}/>{partnerSaving ? 'Saving...' : 'Save'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
